import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Factory, PackageCheck, ScanLine, Truck } from 'lucide-react';
import { formatQty } from '@erp/shared';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { PageHeader, ErrorState, EmptyState } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import { Badge, StatusBadge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { toast } from 'sonner';

interface LookupResult {
  unit: { id: string; qrCode: string; status: string; widthMm: number; heightMm: number; modelName: string; modelCode: string };
  packaging: { id: string; code: string; name: string; mode: string; sku: string; cost: number };
  stock: { physical: number; reserved: number; available: number; incoming: number; projected: number };
  canPack: boolean;
  action: string;
}

interface PackResult {
  unit: { id: string; qr: string; model: string; widthMm: number; heightMm: number };
  packaging: { code: string; name: string };
  orderNumber: string;
  produced: boolean;
  stockAvailableBefore: number;
  panotec: { reference: string; status: string };
}

interface PackagingOrderRow {
  id: string; number: string; status: string; created_at: string; completed_at: string | null;
  packaging_code: string; packaging_name: string; mode: string; qr_code: string | null;
  model_name: string | null; panotec_status: string | null;
}

function beep(ok: boolean) {
  try {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.frequency.value = ok ? 1046 : 220;
    oscillator.type = 'sine';
    gain.gain.value = 0.06;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + (ok ? 0.1 : 0.3));
    setTimeout(() => void context.close(), 400);
  } catch { /* audio no disponible */ }
}

export function PackagingPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState('');
  const [lookup, setLookup] = useState<LookupResult | null>(null);
  const [result, setResult] = useState<PackResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [packing, setPacking] = useState(false);

  const ordersQuery = useQuery({
    queryKey: ['packaging-orders'],
    queryFn: () => api<{ data: PackagingOrderRow[] }>('/packaging/orders'),
    refetchInterval: 30_000
  });

  useEffect(() => { inputRef.current?.focus(); }, []);

  const search = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim()) return;
    setLoading(true); setError(null); setResult(null); setLookup(null);
    try {
      const data = await api<LookupResult>(`/packaging/lookup?qr=${encodeURIComponent(code.trim())}`);
      setLookup(data);
      if (!data.canPack) beep(false);
    } catch (err) {
      beep(false);
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const pack = async () => {
    if (!lookup) return;
    setPacking(true); setError(null);
    try {
      const data = await api<PackResult>('/packaging/pack', { method: 'POST', body: { qr: lookup.unit.qrCode } });
      setResult(data);
      setLookup(null);
      setCode('');
      beep(true);
      toast.success(`Unidad embalada · ${data.orderNumber}`);
      await qc.invalidateQueries({ queryKey: ['packaging-orders'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      await qc.invalidateQueries({ queryKey: ['units'] });
    } catch (err) {
      beep(false);
      setError(errorMessage(err));
    } finally {
      setPacking(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div>
      <PageHeader
        title="Puesto de embalaje"
        description="Escanea el QR del espejo terminado: el ERP selecciona o fabrica el embalaje Panotec"
        badge={can('packaging.write') ? <Badge tone="success">Terminal operativo</Badge> : <Badge tone="neutral">Solo lectura</Badge>}
      />

      <div className="grid gap-4 xl:grid-cols-[1.2fr_1fr]">
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-5">
              <form onSubmit={search} className="flex gap-2">
                <div className="relative flex-1">
                  <ScanLine className="pointer-events-none absolute left-3 top-1/2 size-6 -translate-y-1/2 text-primary" />
                  <input
                    ref={inputRef}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Escanear QR del espejo (ESP-…)"
                    className="scan-input pl-12 uppercase"
                    autoComplete="off"
                  />
                </div>
                <Button type="submit" className="h-14 px-6" loading={loading}>Buscar</Button>
              </form>
              <p className="mt-2 text-[11px] text-muted-foreground">El lector debe enviar Enter. El QR se imprime al iniciar el kit y viaja con la unidad hasta la expedición.</p>
            </CardContent>
          </Card>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
              <p className="font-semibold">No se puede continuar</p>
              <p className="mt-1 text-xs">{error}</p>
            </div>
          ) : null}

          {lookup ? (
            <Card>
              <CardHeader
                title={lookup.unit.modelName}
                description={`${lookup.unit.qrCode} · ${lookup.unit.widthMm}×${lookup.unit.heightMm} mm`}
                actions={<StatusBadge status={lookup.unit.status} />}
              />
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-border bg-muted/20 p-3">
                    <p className="label-xs">Embalaje requerido</p>
                    <p className="mt-1 text-sm font-semibold">{lookup.packaging.name}</p>
                    <p className="font-mono text-[10px] text-muted-foreground">{lookup.packaging.code} · modo {lookup.packaging.mode}</p>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/20 p-3">
                    <p className="label-xs">Stock de cajas</p>
                    <p className="mt-1 text-sm font-semibold">{formatQty(lookup.stock.available)} ud disponibles</p>
                    <p className="text-[10px] text-muted-foreground">{lookup.stock.available >= 1 ? 'Se consumirá del stock' : 'Se fabricará ahora con cartón'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-300">
                  <Factory className="mt-0.5 size-4 shrink-0" />
                  <div>
                    <p className="font-semibold">Acción del ERP</p>
                    <p className="mt-0.5">{lookup.action}</p>
                  </div>
                </div>

                <Button className="h-11 w-full" size="lg" disabled={!lookup.canPack || !can('packaging.write')} loading={packing} onClick={() => void pack()}>
                  <PackageCheck /> {lookup.stock.available >= 1 ? 'Empaquetar y consumir stock' : 'Fabricar embalaje, consumir cartón y empaquetar'}
                </Button>
                {!lookup.canPack ? <p className="text-center text-[11px] text-amber-600">La unidad no está en producción. Completa su kit primero.</p> : null}
              </CardContent>
            </Card>
          ) : null}

          {result ? (
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400"><CheckCircle2 className="size-5" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">Unidad lista para logística</p>
                    <p className="mt-1 text-xs text-muted-foreground">{result.unit.qr} · {result.unit.model}</p>
                    <div className="mt-3 grid gap-2 text-[11px] sm:grid-cols-2">
                      <div className="rounded border border-border p-2"><span className="text-muted-foreground">Orden</span><p className="font-mono font-medium">{result.orderNumber}</p></div>
                      <div className="rounded border border-border p-2"><span className="text-muted-foreground">Panotec</span><p className="font-mono font-medium">{result.panotec.reference}</p></div>
                      <div className="rounded border border-border p-2"><span className="text-muted-foreground">Embalaje</span><p className="font-medium">{result.produced ? 'Fabricado ahora' : 'Consumido de stock'}</p></div>
                      <div className="rounded border border-border p-2"><span className="text-muted-foreground">Siguiente paso</span><p className="font-medium">Pegar QR exterior y enviar a logística</p></div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>

        <Card>
          <CardHeader title="Órdenes de embalaje" description="Últimos trabajos Panotec" actions={<Button variant="ghost" size="sm" onClick={() => void ordersQuery.refetch()}>Actualizar</Button>} />
          {ordersQuery.error ? <ErrorState message={(ordersQuery.error as Error).message} onRetry={() => void ordersQuery.refetch()} /> : ordersQuery.data?.data.length === 0 ? (
            <EmptyState title="Sin órdenes" description="Las órdenes se crean al escanear una unidad." icon={PackageCheck} />
          ) : (
            <div className="max-h-[560px] overflow-y-auto divide-y divide-border">
              {ordersQuery.data?.data.map((order) => (
                <div key={order.id} className="px-4 py-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono text-xs font-medium">{order.number}</p>
                    <StatusBadge status={order.status} />
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">{order.packaging_name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                    {order.qr_code ? <span className="font-mono">{order.qr_code}</span> : null}
                    {order.panotec_status ? <StatusBadge status={order.panotec_status} /> : null}
                    <span>{new Date(order.created_at).toLocaleString('es-ES')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="border-t border-border p-3">
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground"><Truck className="size-3" /> Al embalar, la unidad queda lista para expedición.</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
